import { Link } from 'react-router-dom';
import { Sparkles, ArrowUpRight, ShieldCheck, Clock3 } from 'lucide-react';

/**
 * SmartProfileEvalCTA — A reusable, conversion-focused widget that points
 * visitors on a country hub page (Germany, UK, Canada, Australia, Ireland)
 * to the existing /study-abroad/free-profile-evaluation/ tool.
 *
 * Design intent:
 *  - Cross-link existing high-conversion calculator (NO new tools).
 *  - Country-themed eyebrow & copy so it doesn't feel generic.
 *  - WCAG-AA contrast (dark slate + gold + white).
 *  - Self-contained: no external props required for default copy.
 *
 * Props:
 *   country      — display label, e.g. "Germany", "UK"
 *   country_slug — used as a query-string source tag for funnel analytics
 *   testId       — outer section data-testid
 */
const SmartProfileEvalCTA = ({
  country = 'Your Target',
  country_slug = 'study-abroad-hub',
  testId,
}) => {
  const href = `/study-abroad/free-profile-evaluation/?source=${country_slug}-smart-cta`;

  return (
    <section
      className="py-12 lg:py-16"
      aria-labelledby="smart-profile-eval-heading"
      data-testid={testId || `${country_slug}-smart-profile-cta`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="max-w-5xl mx-auto rounded-3xl overflow-hidden relative text-white"
          style={{
            background:
              'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)',
          }}
        >
          {/* Soft gold glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C5A028]/16 blur-3xl"
          />

          <div className="relative p-7 sm:p-10 lg:p-12 grid lg:grid-cols-5 gap-6 lg:gap-10 items-center">
            {/* Left copy */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/16 border border-[#C5A028]/40 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                Free Profile Evaluation · 60 seconds
              </div>
              <h2
                id="smart-profile-eval-heading"
                className="text-2xl sm:text-3xl lg:text-[34px] font-bold font-syne leading-tight mb-3"
              >
                Will you qualify to study in {country}?
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-5 max-w-lg">
                Take our free, 60-second eligibility check — academic profile,
                budget, target intake — and get an instant snapshot of your
                chances + a senior counsellor follow-up on WhatsApp.
              </p>

              <ul className="space-y-2 text-xs sm:text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <Clock3 className="w-4 h-4 text-[#C5A028] flex-shrink-0" />
                  <span>Takes under 60 seconds — no signup wall</span>
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#C5A028] flex-shrink-0" />
                  <span>Confidential · No spam · MEA-approved consultancy</span>
                </li>
              </ul>
            </div>

            {/* Right CTA block (2 cols) */}
            <div className="lg:col-span-2 flex flex-col items-stretch gap-3">
              <Link
                to={href}
                className="group inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-6 py-4 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#C5A028]/40"
                data-testid={`${country_slug}-smart-cta-primary`}
              >
                Start My Free Evaluation
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/study-abroad/book-counselling/"
                className="text-center text-xs sm:text-sm text-slate-300 hover:text-white underline-offset-4 hover:underline transition-colors"
                data-testid={`${country_slug}-smart-cta-secondary`}
              >
                Or speak to a counsellor directly →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartProfileEvalCTA;
